import { useCallback, useMemo, useEffect } from 'react';
import { useLocalStorage } from 'store/hooks/useLocalStorage';
import { isProduction } from 'utils';
import { useCobrand } from 'features/cobrand/hooks/useCobrandUtils';
import {
  FEATURE_FLAGS,
  FEATURE_FLAGS_STORAGE_KEY,
  FeatureFlagKey,
  getEnvDefaultForFlag,
} from '../config/constants';

interface FeatureFlagState {
  [key: string]: boolean | string;
}

/**
 * Hook to manage feature flags with hybrid approach
 *
 * Priority:
 * - Non-production: localStorage → CI/CD Environment Variables → default (false)
 * - Production: CI/CD Environment Variables only → default (false)
 *
 * - In production: Only CI/CD environment variables are used
 * - In non-production: localStorage takes priority (for testing), then CI/CD variables
 *
 * Only stores explicitly enabled (true) flags in localStorage to keep it minimal
 *
 * @returns Object with feature flag utilities
 */
export const useFeatureFlags = () => {
  const { cobrand } = useCobrand();
  const [flags, setFlags, clearFlags] = useLocalStorage<FeatureFlagState>(
    FEATURE_FLAGS_STORAGE_KEY,
    {},
  );

  /**
   * Get the environment variable value for a feature flag
   * @param key - The feature flag key
   * @returns The raw environment variable value, or undefined if not set
   */
  const getEnvValue = useCallback((key: FeatureFlagKey): string | undefined => {
    const envVarName = `REACT_APP_${key}`;
    return process.env[envVarName];
  }, []);

  /**
   * Parse and normalize an environment variable value
   * @param envValue - Raw environment variable value
   * @returns Normalized boolean or string value
   */
  const parseEnvValue = useCallback((envValue: string): boolean | string => {
    const trimmed = envValue.trim().toLowerCase();
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    return envValue; // Comma-separated cobrand list
  }, []);

  /**
   * Build initial flags state from CI/CD environment variables
   * @returns FeatureFlagState object with parsed env values
   */
  const initializeFromEnvVariables = useCallback(
    (): FeatureFlagState =>
      FEATURE_FLAGS.reduce<FeatureFlagState>((acc, flag) => {
        const envValue = getEnvValue(flag.key as FeatureFlagKey);
        if (envValue !== undefined) {
          acc[flag.key] = parseEnvValue(envValue);
        }
        return acc;
      }, {}),
    [getEnvValue, parseEnvValue],
  );

  /**
   * Initialize localStorage with CI/CD values in non-production environments
   * This ensures the toggle reflects the actual effective state from the start
   */
  useEffect(() => {
    if (isProduction() || Object.keys(flags).length > 0) {
      return; // Skip if in production or localStorage already has values
    }

    const initialFlags = initializeFromEnvVariables();
    if (Object.keys(initialFlags).length > 0) {
      setFlags(initialFlags);
    }
  }, [flags, setFlags, initializeFromEnvVariables]);

  /**
   * Evaluate a localStorage override value against an optional cobrand
   * Matches CI/CD format: boolean or comma-separated string only
   * @param override - The stored override value (boolean or string)
   * @param targetCobrand - Optional cobrand to check against
   * @returns Whether the feature is enabled
   */
  const evaluateLocalStorageOverride = useCallback(
    (override: boolean | string, targetCobrand?: string): boolean => {
      if (typeof override === 'boolean') {
        return override;
      }
      if (typeof override === 'string') {
        const trimmed = override.trim().toLowerCase();

        if (trimmed === 'true') {
          return true;
        }
        if (trimmed === 'false') {
          return false;
        }
        const allowedCobrands = trimmed
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        if (!targetCobrand) {
          return false;
        }

        const result = allowedCobrands.includes(targetCobrand.toLowerCase());
        return result;
      }

      return false;
    },
    [],
  );

  /**
   * Internal helper to check if a feature flag is enabled for a specific cobrand
   * Priority: localStorage (non-prod only) → CI/CD Environment Variable → default (false)
   *
   * @param key - The feature flag key
   * @param targetCobrand - Cobrand name to check against (e.g., 'bcbskc', 'hphc')
   * @returns Whether the feature is enabled for the given context
   */
  const checkEnabled = useCallback(
    (key: FeatureFlagKey, targetCobrand: string): boolean => {
      // In non-production, check localStorage first (allows testing overrides)
      if (!isProduction()) {
        const userOverride = flags[key];

        if (userOverride !== undefined) {
          const result = evaluateLocalStorageOverride(userOverride, targetCobrand);
          return result;
        }
      }
      const envDefault = getEnvDefaultForFlag(key, targetCobrand);

      if (envDefault !== undefined) {
        return envDefault;
      }
      return false;
    },
    [flags, evaluateLocalStorageOverride],
  );

  /**
   * Check if a feature flag is enabled for the current cobrand
   * Auto-detects the current cobrand from the URL/context
   *
   * @param key - The feature flag key
   * @returns Whether the feature is enabled for the current user's cobrand
   */
  const isEnabled = useCallback(
    (key: FeatureFlagKey): boolean => checkEnabled(key, cobrand.id),
    [checkEnabled, cobrand.id],
  );

  /**
   * Check if a feature flag is enabled for a specific cobrand
   * Use this when you need to check a different cobrand than the current one
   *
   * @param key - The feature flag key
   * @param cobrandId - Specific cobrand to check (e.g., 'hphc', 'bcbskc')
   * @returns Whether the feature is enabled for the specified cobrand
   */
  const isEnabledForCobrand = useCallback(
    (key: FeatureFlagKey, cobrandId: string): boolean => checkEnabled(key, cobrandId),
    [checkEnabled],
  );

  /**
   * Set a specific feature flag value
   * Matches CI/CD format: boolean or comma-separated string only
   *
   * @param key - The feature flag key
   * @param value - Boolean (true/false) or string (comma-separated cobrands like 'bcbskc,bcbssc')
   */
  const setFlag = useCallback(
    (key: FeatureFlagKey, value: boolean | string) => {
      // Store the value (boolean, string)
      // Note: We store false values to allow overriding CI/CD true values
      setFlags({
        ...flags,
        [key]: value,
      });
    },
    [flags, setFlags],
  );

  /**
   * Toggle a specific feature flag
   * Since localStorage is initialized with CI/CD values, we toggle the stored value
   * If cobrand-specific (string), toggles to boolean true (all cobrands)
   */
  const toggleFlag = useCallback(
    (key: FeatureFlagKey) => {
      const currentValue = flags[key];
      if (currentValue !== undefined) {
        if (typeof currentValue === 'boolean') {
          setFlag(key, !currentValue);
        } else {
          setFlag(key, true);
        }
        return;
      }
      setFlag(key, true);
    },
    [flags, setFlag],
  );

  /**
   * Get all feature flags with their metadata and effective enabled state
   * Considers CI/CD environment variables and localStorage (non-prod only)
   */
  const getAllFlags = useMemo(() => {
    const result = FEATURE_FLAGS.map((flag) => {
      const enabled = isEnabled(flag.key);
      return {
        ...flag,
        enabled,
      };
    });
    return result;
  }, [isEnabled]);

  /**
   * Get a human-readable display value for the current flag state
   * Shows localStorage value if present, otherwise CI/CD environment variable
   * @param key - The feature flag key
   * @returns Display string like 'off', 'on (all cobrands)', or 'on (hphc,medica)'
   */
  const getCurrentValue = useCallback(
    (key: FeatureFlagKey): string => {
      const value = flags[key];
      if (value !== undefined) {
        if (typeof value === 'boolean') return value ? 'on (all cobrands)' : 'off';
        if (typeof value === 'string') {
          const trimmed = value.trim().toLowerCase();
          if (trimmed === 'true') return 'on (all cobrands)';
          if (trimmed === 'false') return 'off';
          return `on (${value})`;
        }
      }
      const envValue = getEnvValue(key);
      if (envValue !== undefined) {
        const trimmed = envValue.trim().toLowerCase();
        if (trimmed === 'true') return 'on (all cobrands)';
        if (trimmed === 'false') return 'off';
        return `on (${envValue})`;
      }
      return 'off';
    },
    [flags, getEnvValue],
  );

  /**
   * Check if a flag is enabled as a boolean (global enable), not cobrand-specific
   * Used for toggle switch state - should only be ON for boolean true or string 'true'
   * Falls back to CI/CD environment value when localStorage is empty
   * @param key - The feature flag key
   * @returns true only if value is boolean true or string 'true'
   */
  const isBooleanEnabled = useCallback(
    (key: FeatureFlagKey): boolean => {
      const value = flags[key];
      if (value !== undefined) {
        if (typeof value === 'boolean') {
          return value;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim().toLowerCase();
          return trimmed === 'true';
        }
      }
      const envValue = getEnvValue(key);
      if (envValue !== undefined) {
        const trimmed = envValue.trim().toLowerCase();
        return trimmed === 'true';
      }

      return false;
    },
    [flags, getEnvValue],
  );

  /**
   * Check if localStorage has an override for this flag
   * Returns true only if the localStorage value differs from CI/CD value
   * @param key - The feature flag key
   * @returns true if localStorage value is different from CI/CD (actual override), false otherwise
   */
  const hasLocalStorageOverride = useCallback(
    (key: FeatureFlagKey): boolean => {
      const localValue = flags[key];
      if (localValue === undefined) {
        return false;
      }
      const envValue = getEnvValue(key);
      if (envValue === undefined) {
        return true;
      }
      const normalizedLocal =
        typeof localValue === 'boolean'
          ? localValue.toString()
          : localValue.toString().toLowerCase().trim();
      const normalizedEnv = envValue.toLowerCase().trim();
      return normalizedLocal !== normalizedEnv;
    },
    [flags, getEnvValue],
  );

  /**
   * Check if a flag is controlled by CI/CD environment variable
   * @param key - The feature flag key
   * @returns true if CI/CD env var exists for this flag
   */
  const isEnvControlled = useCallback(
    (key: FeatureFlagKey): boolean => getEnvDefaultForFlag(key) !== undefined,
    [],
  );

  /**
   * Parse the current cobrand list from a flag value
   * Returns empty array for boolean or undefined values
   * @param key - The feature flag key
   * @returns Array of cobrand names
   */
  const getCurrentCobrands = useCallback(
    (key: FeatureFlagKey): string[] => {
      const value = getCurrentValue(key);
      // Extract cobrands from "on (hphc,medica)" format
      // Don't match "on (all cobrands)" - that's a boolean enable, not a cobrand list
      const match = value.match(/^on \((.+)\)$/);
      if (match && match[1] !== 'all cobrands') {
        return match[1]
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    },
    [getCurrentValue],
  );

  /**
   * Add cobrands to a feature flag
   * Merges with existing cobrands and removes duplicates
   * @param key - The feature flag key
   * @param newCobrands - Comma-separated string or array of cobrand names
   */
  const addCobrands = useCallback(
    (key: FeatureFlagKey, newCobrands: string | string[]) => {
      const cobrandsArray = Array.isArray(newCobrands)
        ? newCobrands
        : newCobrands
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);

      const existingCobrands = getCurrentCobrands(key);
      const allCobrands = Array.from(
        new Set([...existingCobrands.map((c) => c.toLowerCase()), ...cobrandsArray]),
      );

      if (allCobrands.length > 0) {
        setFlag(key, allCobrands.join(','));
      }
    },
    [getCurrentCobrands, setFlag],
  );

  /**
   * Remove a specific cobrand from a feature flag
   * If no cobrands remain, sets flag to false
   * @param key - The feature flag key
   * @param cobrandToRemove - The cobrand name to remove
   */
  const removeCobrand = useCallback(
    (key: FeatureFlagKey, cobrandToRemove: string) => {
      const existingCobrands = getCurrentCobrands(key);
      const filteredCobrands = existingCobrands.filter(
        (c) => c.toLowerCase() !== cobrandToRemove.toLowerCase(),
      );

      if (filteredCobrands.length === 0) {
        setFlag(key, false);
      } else {
        setFlag(key, filteredCobrands.join(','));
      }
    },
    [getCurrentCobrands, setFlag],
  );

  /**
   * Get helper text for the cobrand input field
   * @param key - The feature flag key
   * @param envValue - Optional CI/CD environment value
   * @returns Helper text string
   */
  const getHelperText = useCallback(
    (key: FeatureFlagKey, envValue?: string): string => {
      if (isBooleanEnabled(key)) {
        return 'Toggle OFF to add specific cobrands';
      }
      if (envValue) {
        return 'Override CI/CD value for local testing';
      }
      return 'Enter comma-separated cobrands to add';
    },
    [isBooleanEnabled],
  );

  return {
    flags,
    isEnabled,
    isEnabledForCobrand,
    toggleFlag,
    setFlag,
    clearFlags,
    getAllFlags,
    getEnvValue,
    getCurrentValue,
    isBooleanEnabled,
    hasLocalStorageOverride,
    isEnvControlled,
    getCurrentCobrands,
    addCobrands,
    removeCobrand,
    getHelperText,
  };
};
